import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { attachAuthInterceptors } from './authInterceptors';

/**
 * Builds a configured axios instance for talking to the backend.
 *
 * `axiosConfig.baseURL` is required — the caller (a resource service) is
 * responsible for sourcing it from the `ApiHandler` constructor, which in
 * turn receives it from `ApiProvider`'s `baseUrl` prop. The previous
 * implementation read `process.env.REACT_APP_API_BASE_URL` directly, which
 * was both the wrong env-var convention for a Bun app and bypassed the
 * provider plumbing entirely.
 *
 * The final base URL is `<baseURL>/api/v1` so resource endpoints can be
 * written as relative paths (e.g. `/sales/revenue-summary`).
 *
 * To bump the API version, change the path segment below — endpoint
 * builders in `handlers/resources/*` remain version-agnostic.
 *
 * A list in the query string is written as repeated keys —
 * `brandIds=a&brandIds=b` — which is how bos-api binds a list parameter
 * (BOS-201). Axios's default, `brandIds[]=a`, binds nothing there.
 */
export const getAxiosClient = (
    axiosConfig: AxiosRequestConfig & { baseURL: string },
): AxiosInstance => {
    if (!axiosConfig.baseURL) {
        throw new Error(
            'getAxiosClient: `baseURL` is required. Set BUN_PUBLIC_API_BASE_URL in your environment so ApiProvider can forward it.',
        );
    }

    const apiBaseUrl = new URL('api/v1', axiosConfig.baseURL).toString();

    const axiosClientRaw = axios.create({
        ...axiosConfig,
        baseURL: apiBaseUrl,
        withCredentials: true,
        paramsSerializer: { indexes: null },
    });

    // Bearer + refresh-on-401 live behind the factory so every resource
    // service inherits them — no per-call interceptor wiring. The refresh
    // single-flight state is module-level, so it coordinates across the
    // separate instances the factory hands out.
    attachAuthInterceptors(axiosClientRaw, apiBaseUrl);

    return axiosClientRaw;
};
